/**
 * Device Database
 * Fire alarm device specifications and current draw data
 */

export interface DeviceSpec {
  id: string;
  name: string;
  manufacturer: string;
  model: string;
  type: 'smoke_detector' | 'heat_detector' | 'pull_station' | 'horn_strobe' | 'speaker' | 'module' | 'relay' | 'duct_detector';
  standbyCurrentMa: number;
  alarmCurrentMa: number;
  voltage: number;
  description?: string;
  nfpaCompliant: boolean;
}

export interface DeviceCategory {
  id: string;
  name: string;
  description: string;
  devices: DeviceSpec[];
}

// Comprehensive device database
export const deviceDatabase: DeviceCategory[] = [
  {
    id: 'initiating_devices',
    name: 'Initiating Devices',
    description: 'Smoke detectors, heat detectors, and manual pull stations',
    devices: [
      {
        id: 'smoke_photo_std',
        name: 'Photoelectric Smoke Detector',
        manufacturer: 'Generic',
        model: 'SD-PHOTO-STD',
        type: 'smoke_detector',
        standbyCurrentMa: 0.05,
        alarmCurrentMa: 0.05,
        voltage: 24,
        description: 'Standard photoelectric smoke detector',
        nfpaCompliant: true
      },
      {
        id: 'smoke_ion_std',
        name: 'Ionization Smoke Detector',
        manufacturer: 'Generic',
        model: 'SD-ION-STD',
        type: 'smoke_detector',
        standbyCurrentMa: 0.08,
        alarmCurrentMa: 0.08,
        voltage: 24,
        description: 'Standard ionization smoke detector',
        nfpaCompliant: true
      },
      {
        id: 'smoke_multi',
        name: 'Multi-Criteria Smoke Detector',
        manufacturer: 'Generic',
        model: 'SD-MULTI',
        type: 'smoke_detector',
        standbyCurrentMa: 0.15,
        alarmCurrentMa: 0.15,
        voltage: 24,
        description: 'Multi-criteria detector with photo, thermal, and CO',
        nfpaCompliant: true
      },
      {
        id: 'heat_fixed',
        name: 'Fixed Temperature Heat Detector',
        manufacturer: 'Generic',
        model: 'HD-FIXED',
        type: 'heat_detector',
        standbyCurrentMa: 0.04,
        alarmCurrentMa: 0.04,
        voltage: 24,
        description: 'Fixed temperature heat detector (135°F/57°C)',
        nfpaCompliant: true
      },
      {
        id: 'heat_ror',
        name: 'Rate-of-Rise Heat Detector',
        manufacturer: 'Generic',
        model: 'HD-ROR',
        type: 'heat_detector',
        standbyCurrentMa: 0.05,
        alarmCurrentMa: 0.05,
        voltage: 24,
        description: 'Rate-of-rise heat detector with fixed temp backup',
        nfpaCompliant: true
      },
      {
        id: 'pull_station_std',
        name: 'Manual Pull Station',
        manufacturer: 'Generic',
        model: 'PS-STD',
        type: 'pull_station',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 0.001,
        voltage: 24,
        description: 'Single-action manual pull station',
        nfpaCompliant: true
      },
      {
        id: 'pull_station_dual',
        name: 'Dual-Action Pull Station',
        manufacturer: 'Generic',
        model: 'PS-DUAL',
        type: 'pull_station',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 0.001,
        voltage: 24,
        description: 'Dual-action manual pull station (ADA compliant)',
        nfpaCompliant: true
      },
      {
        id: 'duct_detector',
        name: 'Duct Smoke Detector',
        manufacturer: 'Generic',
        model: 'DD-STD',
        type: 'duct_detector',
        standbyCurrentMa: 0.1,
        alarmCurrentMa: 0.1,
        voltage: 24,
        description: 'In-duct smoke detector for HVAC systems',
        nfpaCompliant: true
      }
    ]
  },
  {
    id: 'notification_appliances',
    name: 'Notification Appliances',
    description: 'Horns, strobes, horn/strobes, and speakers',
    devices: [
      {
        id: 'horn_strobe_15cd',
        name: 'Horn/Strobe 15cd',
        manufacturer: 'Generic',
        model: 'HS-15',
        type: 'horn_strobe',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 95,
        voltage: 24,
        description: '15 candela horn/strobe combination',
        nfpaCompliant: true
      },
      {
        id: 'horn_strobe_30cd',
        name: 'Horn/Strobe 30cd',
        manufacturer: 'Generic',
        model: 'HS-30',
        type: 'horn_strobe',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 110,
        voltage: 24,
        description: '30 candela horn/strobe combination',
        nfpaCompliant: true
      },
      {
        id: 'horn_strobe_75cd',
        name: 'Horn/Strobe 75cd',
        manufacturer: 'Generic',
        model: 'HS-75',
        type: 'horn_strobe',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 155,
        voltage: 24,
        description: '75 candela horn/strobe combination',
        nfpaCompliant: true
      },
      {
        id: 'horn_strobe_110cd',
        name: 'Horn/Strobe 110cd',
        manufacturer: 'Generic',
        model: 'HS-110',
        type: 'horn_strobe',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 195,
        voltage: 24,
        description: '110 candela horn/strobe combination',
        nfpaCompliant: true
      },
      {
        id: 'horn_strobe_177cd',
        name: 'Horn/Strobe 177cd',
        manufacturer: 'Generic',
        model: 'HS-177',
        type: 'horn_strobe',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 250,
        voltage: 24,
        description: '177 candela horn/strobe combination',
        nfpaCompliant: true
      },
      {
        id: 'speaker_std',
        name: 'Speaker (25V)',
        manufacturer: 'Generic',
        model: 'SPK-25',
        type: 'speaker',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 40,
        voltage: 25,
        description: '25V speaker for voice evacuation',
        nfpaCompliant: true
      },
      {
        id: 'speaker_strobe',
        name: 'Speaker/Strobe 15cd',
        manufacturer: 'Generic',
        model: 'SPKS-15',
        type: 'speaker',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 135,
        voltage: 24,
        description: 'Speaker with 15cd strobe combination',
        nfpaCompliant: true
      }
    ]
  },
  {
    id: 'modules',
    name: 'Control Modules',
    description: 'Monitor modules, control modules, and relay modules',
    devices: [
      {
        id: 'monitor_module',
        name: 'Monitor Module',
        manufacturer: 'Generic',
        model: 'MM-STD',
        type: 'module',
        standbyCurrentMa: 0.35,
        alarmCurrentMa: 0.35,
        voltage: 24,
        description: 'Single input monitor module',
        nfpaCompliant: true
      },
      {
        id: 'control_module',
        name: 'Control Module',
        manufacturer: 'Generic',
        model: 'CM-STD',
        type: 'module',
        standbyCurrentMa: 0.35,
        alarmCurrentMa: 0.35,
        voltage: 24,
        description: 'Single output control module',
        nfpaCompliant: true
      },
      {
        id: 'relay_module',
        name: 'Relay Module',
        manufacturer: 'Generic',
        model: 'RM-STD',
        type: 'relay',
        standbyCurrentMa: 0.35,
        alarmCurrentMa: 25,
        voltage: 24,
        description: 'Form-C relay module',
        nfpaCompliant: true
      },
      {
        id: 'isolator_module',
        name: 'Isolator Module',
        manufacturer: 'Generic',
        model: 'ISO-STD',
        type: 'module',
        standbyCurrentMa: 0.001,
        alarmCurrentMa: 0.001,
        voltage: 24,
        description: 'Short circuit isolator module',
        nfpaCompliant: true
      }
    ]
  }
];

/**
 * Get all devices as a flat array
 */
export function getAllDevices(): DeviceSpec[] {
  return deviceDatabase.flatMap(category => category.devices);
}

/**
 * Get device by ID
 */
export function getDeviceById(id: string): DeviceSpec | undefined {
  return getAllDevices().find(device => device.id === id);
}

/**
 * Get devices by type
 */
export function getDevicesByType(type: DeviceSpec['type']): DeviceSpec[] {
  return getAllDevices().filter(device => device.type === type);
}

/**
 * Search devices by name or model
 */
export function searchDevices(query: string): DeviceSpec[] {
  const lowerQuery = query.toLowerCase();
  return getAllDevices().filter(device => 
    device.name.toLowerCase().includes(lowerQuery) ||
    device.model.toLowerCase().includes(lowerQuery) ||
    device.manufacturer.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get all unique manufacturers
 */
export function getAllManufacturers(): string[] {
  const manufacturers = new Set(getAllDevices().map(device => device.manufacturer));
  return Array.from(manufacturers).sort();
}

/**
 * Get all categories
 */
export function getAllCategories(): DeviceCategory[] {
  return deviceDatabase;
}
